/**
 * Maps backend error codes and HTTP status codes to user-friendly messages.
 *
 * Usage:
 *   import { getErrorMessage } from '../utils/errorMessages';
 *   const msg = getErrorMessage('DIGIPART_SESSION_EXPIRED');
 *   const msg = getErrorMessage(undefined, 429);
 */
import axios from 'axios';

const CODE_MESSAGES: Record<string, string> = {
  // DigiPART errors
  DIGIPART_AUTHENTICATION_FAILED:
    'DigiPART login failed. Please check your credentials and try again.',
  DIGIPART_SESSION_EXPIRED:
    'Your DigiPART session expired. Reconnect via your Profile page, then resume the job.',
  DIGIPART_GRAPHQL_ERROR:
    'DigiPART returned an unexpected response. Please try again.',
  DIGIPART_BATCH_PARTIAL:
    'Some parts could not be fetched from DigiPART. Processing continued with available data.',
  DIGIPART_PART_NOT_FOUND:
    'One or more parts were not found in DigiPART. Verify the part IDs and try again.',
  DIGIPART_MUTATION_FAILED:
    'DigiPART rejected the dimension update. Please review the data and try again.',
  DIGIPART_NETWORK_TIMEOUT:
    'DigiPART did not respond in time. Please check your network and try again.',
  DIGIPART_INVALID_RESPONSE:
    'DigiPART returned an unrecognised response format.',

  // AI errors
  AI_RATE_LIMIT:
    'The AI service is temporarily rate-limited. The system will retry automatically.',
  AI_QUOTA_EXHAUSTED:
    'Your AI quota has been exhausted. Contact your administrator to increase your limit.',
  AI_PARSE_FAILURE:
    'The AI response could not be parsed. This part may be skipped.',
  AI_INVALID_RESPONSE:
    'The AI returned an unexpected response format.',
  AI_CLIENT_NOT_INITIALIZED:
    'The AI client is not configured. Check your API key in Profile → Settings.',
  AI_CONTENT_FILTERED:
    'The AI filtered this content. The part has been skipped.',

  // Pipeline errors
  PIPELINE_WORKER_TIMEOUT:
    'The processing worker ran out of time. Your progress is saved — resume the job to continue.',
  PIPELINE_INITIALIZATION_FAILED:
    'The pipeline could not initialise. Check the job configuration and try again.',
  PIPELINE_PHASE_FAILED:
    'A processing phase failed. Partial results may be available.',
  PIPELINE_INVALID_CONFIG:
    'The job configuration is invalid. Please review the settings and restart.',
  PIPELINE_NO_PARTS_SPECIFIED:
    'No part IDs were specified for this job. Please add part IDs and try again.',
  PIPELINE_UNKNOWN:
    'An unexpected pipeline error occurred.',

  // Auth errors
  AUTH_NO_DIGIPART_ACCOUNT:
    'No DigiPART account is assigned to your user. Contact your administrator.',
  AUTH_INVALID_API_KEY:
    'Your AI API key is invalid or expired. Update it in Profile → API Keys.',
  AUTH_SESSION_NOT_ESTABLISHED:
    'DigiPART session could not be established. Please log in via Profile → DigiPART Connection.',
};

const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was invalid. Please check your input.',
  401: 'You are not authorised. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. The resource may already exist.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'A server error occurred. Please try again.',
  502: 'The server is temporarily unavailable. Please try again shortly.',
  503: 'The service is currently unavailable. Please try again shortly.',
};

/**
 * Returns a user-friendly message for a given error code and/or HTTP status.
 *
 * @param code   - Machine-readable error code (e.g. 'DIGIPART_SESSION_EXPIRED').
 * @param httpStatus - HTTP status code (e.g. 429).
 * @returns A human-readable string suitable for display in the UI.
 */
export function getErrorMessage(code?: string, httpStatus?: number): string {
  if (code && CODE_MESSAGES[code]) {
    return CODE_MESSAGES[code];
  }
  if (httpStatus && HTTP_STATUS_MESSAGES[httpStatus]) {
    return HTTP_STATUS_MESSAGES[httpStatus];
  }
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Type-safe extraction of an API error message from an unknown catch value.
 *
 * Use in every catch block instead of `(err: any).response?.data?.detail`.
 *
 * @param err      - The unknown value caught in a catch block.
 * @param fallback - Message to return when the error is not an Axios error or has no detail.
 * @returns A string suitable for display in the UI.
 */
/**
 * Convert a raw backend parse-warning string into a plain-English sentence
 * suitable for display to end users.
 *
 * Backend warnings follow the pattern "<prefix>: <detail>". This function
 * maps every known prefix to a friendly message and falls back gracefully for
 * any unrecognised format so raw technical strings never reach the UI.
 *
 * @param raw - Raw warning string from processing_details.parse_warnings[]
 * @returns A user-facing plain-English description of the warning.
 */
export function formatParseWarning(raw: string): string {
  if (!raw) return 'An unknown processing note was recorded.';

  // parse_truncation_recovered: truncated JSON recovered — N rows salvaged
  const truncRecovered = raw.match(/^parse_truncation_recovered:.*?(\d+) rows? salvaged/);
  if (truncRecovered) {
    return `The AI response was cut short; ${truncRecovered[1]} part(s) were recovered and included automatically.`;
  }

  // json_decode_error: <technical Python/JSON detail>
  if (raw.startsWith('json_decode_error:')) {
    return 'The AI response was cut short or malformed — some parts from this batch may be missing.';
  }

  // parse_fail: empty response from AI
  if (raw === 'parse_fail: empty response from AI') {
    return 'The AI returned an empty response for one batch — no parts from this batch were categorised.';
  }

  // parse_fail: <any other detail>
  if (raw.startsWith('parse_fail:')) {
    return 'One batch could not be processed — the AI response was in an unexpected format.';
  }

  // taxonomy_miss: AI category not in taxonomy: 'CATEGORY_NAME'
  const taxonomyMiss = raw.match(/^taxonomy_miss: AI category not in taxonomy: '(.+)'$/);
  if (taxonomyMiss) {
    return `"${taxonomyMiss[1]}" is not a recognised category — that part needs manual review.`;
  }

  // taxonomy_miss: empty category name
  if (raw === 'taxonomy_miss: empty category name') {
    return 'The AI did not suggest a category for one part — that part needs manual review.';
  }

  // taxonomy_casing_mismatch: 'NAME' matched via lowercase lookup
  const casingMismatch = raw.match(/^taxonomy_casing_mismatch: '(.+)' matched via lowercase lookup/);
  if (casingMismatch) {
    return `Category "${casingMismatch[1]}" was matched using a case-insensitive lookup.`;
  }

  // row_PARTNUM_dedup: duplicate — kept first occurrence
  const dedup = raw.match(/^row_(.+)_dedup: duplicate/);
  if (dedup) {
    return `Part "${dedup[1]}" appeared twice in the AI response; only the first result was kept.`;
  }

  // row_PARTNUM_skip: missing 'Part Category 1'
  const skipMissingCat = raw.match(/^row_(.+)_skip: missing 'Part Category 1'/);
  if (skipMissingCat) {
    return `Part "${skipMissingCat[1]}" was skipped — the AI did not provide a category.`;
  }

  // row_N_skip: missing 'Part Number'
  if (raw.match(/^row_\d+_skip: missing 'Part Number'/)) {
    return 'One AI result was skipped — the part number was missing from the response.';
  }

  // row_N_skip: not a dict
  if (raw.match(/^row_\d+_skip: not a dict/)) {
    return 'One AI result was skipped — the response contained an unexpected data type.';
  }

  // Fallback — strip the prefix colon if present, otherwise return as-is
  const colonIdx = raw.indexOf(':');
  if (colonIdx !== -1) {
    return raw.substring(colonIdx + 2).trim();
  }
  return raw;
}

export function extractApiError(err: unknown, fallback = 'An unexpected error occurred. Please try again.'): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data?.detail as string | undefined)
      ?? (err.response?.data?.error as string | undefined)
      ?? (err.response?.data?.message as string | undefined)
      ?? fallback;
  }
  // For plain objects with a response shape (e.g. mock rejections in tests)
  if (err !== null && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: string; error?: string; message?: string } } }).response;
    return response?.data?.detail
      ?? response?.data?.error
      ?? response?.data?.message
      ?? fallback;
  }
  return fallback;
}
